import { useMutation, useQuery } from "react-query";
import config from "../../config";
import type { ProblemDetails } from "../../shared/types/problem-details.type";
import { getAsync, postAsync } from "../protect-api";
import type {
  CreateOrderAuditPayload,
  CreateOrderReportPayload as CreateAmlAlertReportPayload,
  CreateProfilePdfReportPayload,
  OrderAuditsResponse,
  OrderReportsResponse,
  ReportQuery,
  ReportsResponse,
  CreateFullAmlPdfReportPayload,
  CreateMonitorSearchPayload,
  OrderMonitorSearchResponse,
  CreateProfilesListReportPayload,
} from "../../modules/generate-reports/report.types";
import { createQueryString } from "../../utils";
import type { PreSignedURLResponse } from "../../shared/types/common.type";
import { apiVersion } from "../../shared/constants/common.constant";

export const AML_ALERT_REPORTS = "aml-alert-reports";
export const AUDIT_REPORTS = "audit-reports";
export const PROFILE_REPORTS = "profile-reports";
export const AML_SCREENING_REPORTS = "aml-screening-reports";
export const MONITOR_SEARCH_REPORTS = "monitor-search-reports";
export const PROFILES_EXPORT_REPORTS = "profiles-export-reports";

export const getReports = async (
  query: ReportQuery
): Promise<ReportsResponse> => {
  try {
    const { data } = await getAsync(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/reports?${createQueryString(
        query
      )}`
    );

    return {
      items: data.items,
      count: data.count ?? data.totalSize ?? "0",
      apiFailed: false,
    };
  } catch {
    return {
      count: "0",
      items: [],
      apiFailed: true,
    };
  }
};

export const useGetReports = (
  queryParams: ReportQuery,
  queryKey: string
): ReturnType<typeof useQuery<ReportsResponse, string>> => {
  return useQuery<ReportsResponse, string>([queryKey, queryParams], () =>
    getReports(queryParams)
  );
};

export const getReportFileDownload = async (
  reportId: string
): Promise<PreSignedURLResponse> => {
  try {
    const { data } = await getAsync(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/reports/${reportId}/download`
    );
    return {
      downloadUrl: data.downloadUrl,
      fileName: data.fileName,
      expiresAt: data.expiresAt,
      apiFailed: false,
    };
  } catch {
    return {
      downloadUrl: "",
      fileName: "",
      expiresAt: "",
      apiFailed: true,
    };
  }
};

const createReport = async <T>(
  endpoint: string,
  payload: unknown
): Promise<T> => {
  const { data } = await postAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/${endpoint}`,
    payload
  );
  return data;
};

export const createAmlAlertReport = async (
  payload: CreateAmlAlertReportPayload
): Promise<OrderReportsResponse> => {
  return createReport<OrderReportsResponse>("reports", payload);
};

export const createAuditReport = async (
  payload: CreateOrderAuditPayload
): Promise<OrderAuditsResponse> => {
  return createReport<OrderAuditsResponse>("reports", payload);
};

export const createMonitorSearchReport = async (
  payload: CreateMonitorSearchPayload
): Promise<OrderMonitorSearchResponse> => {
  const enhancedPayload = {
    ...payload,
    criteria: {
      ...payload.criteria,
      isScheduled: true,
    },
  };
  return createReport<OrderMonitorSearchResponse>("reports", enhancedPayload);
};

export const useCreateAuditReport = (): ReturnType<
  typeof useMutation<
    OrderAuditsResponse,
    ProblemDetails,
    CreateOrderAuditPayload
  >
> =>
  useMutation<OrderAuditsResponse, ProblemDetails, CreateOrderAuditPayload>(
    createAuditReport
  );

export const useCreateAmlAlertReport = (): ReturnType<
  typeof useMutation<
    OrderReportsResponse,
    ProblemDetails,
    CreateAmlAlertReportPayload
  >
> =>
  useMutation<
    OrderReportsResponse,
    ProblemDetails,
    CreateAmlAlertReportPayload
  >(createAmlAlertReport);

export const useCreateMonitorSearchReport = (): ReturnType<
  typeof useMutation<
    OrderMonitorSearchResponse,
    ProblemDetails,
    CreateMonitorSearchPayload
  >
> =>
  useMutation<
    OrderMonitorSearchResponse,
    ProblemDetails,
    CreateMonitorSearchPayload
  >(createMonitorSearchReport);

export const createProfilePdfReport = async (
  payload: CreateProfilePdfReportPayload
): Promise<OrderReportsResponse> => {
  return createReport<OrderReportsResponse>("reports", payload);
};

export const createFullAmlPdfReport = async (
  payload: CreateFullAmlPdfReportPayload
): Promise<OrderReportsResponse> => {
  return createReport<OrderReportsResponse>("reports", payload);
};

export const useCreateProfilePdfReport = (): ReturnType<
  typeof useMutation<
    OrderReportsResponse,
    ProblemDetails,
    CreateProfilePdfReportPayload
  >
> =>
  useMutation<
    OrderReportsResponse,
    ProblemDetails,
    CreateProfilePdfReportPayload
  >(createProfilePdfReport);

export const useCreateFullAmlPdfReport = (): ReturnType<
  typeof useMutation<
    OrderReportsResponse,
    ProblemDetails,
    CreateFullAmlPdfReportPayload
  >
> =>
  useMutation<
    OrderReportsResponse,
    ProblemDetails,
    CreateFullAmlPdfReportPayload
  >(createFullAmlPdfReport);

export const createProfilesListReport = async (
  payload: CreateProfilesListReportPayload
): Promise<OrderReportsResponse> => {
  return createReport<OrderReportsResponse>("reports", payload);
};

export const useCreateProfilesListReport = (): ReturnType<
  typeof useMutation<
    OrderReportsResponse,
    ProblemDetails,
    CreateProfilesListReportPayload
  >
> =>
  useMutation<
    OrderReportsResponse,
    ProblemDetails,
    CreateProfilesListReportPayload
  >(createProfilesListReport);
